import { defineStore } from 'pinia'
import { reactive, ref, watch } from 'vue'
import i18n from '@/lang/index.js'
import { queryBusinessDictionary } from '@/api/dictionary/index.js'
import {
  getConfiguredDictionaryProjects,
  getDictionaryLanguageCode,
} from '@/utils/businessDict.js'

const STORAGE_KEY = 'business-dictionary-cache'
const LEGACY_STORAGE_KEY = 'business_dictionary_projects_v1'
const inflightProjects = new Map()

function readLocalCache() {
  try {
    const currentCache = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
    if (currentCache.projects && typeof currentCache.projects === 'object') {
      return {
        langCode: currentCache.langCode || '',
        projects: currentCache.projects,
      }
    }

    // 兼容需求开发初期使用过的旧缓存结构，成功刷新后统一迁移到标准缓存键。
    const legacyCache = JSON.parse(localStorage.getItem(LEGACY_STORAGE_KEY) || '{}')
    return {
      langCode: '',
      projects: legacyCache.projects || legacyCache,
    }
  } catch {
    localStorage.removeItem(STORAGE_KEY)
    return { langCode: '', projects: {} }
  }
}

function normalizeProjectCodes(projectCodes) {
  return [...new Set((Array.isArray(projectCodes) ? projectCodes : [projectCodes]).filter(Boolean))]
}

function isResponseSuccess(response) {
  if (response?.success === false) return false
  if (response?.code !== undefined && response?.code !== null) {
    // 字典服务成功码为 200，兼容部分环境沿用的 0，与全局请求层保持一致。
    return ['0', '200'].includes(String(response.code))
  }
  return response?.success === true
}

const useBusinessDictStore = defineStore('businessDict', () => {
  const localCache = readLocalCache()
  const projects = ref(localCache.projects)
  const cacheLangCode = ref(localCache.langCode)
  const checkedProjects = ref([])
  const loadingProjects = reactive({})
  const errors = reactive({})
  const trackedProjects = new Set([
    ...Object.keys(localCache.projects),
    ...getConfiguredDictionaryProjects(),
  ])
  let languageRefreshId = 0

  function persist() {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ langCode: cacheLangCode.value, projects: projects.value }),
    )
    localStorage.removeItem(LEGACY_STORAGE_KEY)
  }

  function getProject(projectCode) {
    return projects.value[projectCode]
  }

  function getGroup(projectCode, groupCode) {
    return getProject(projectCode)?.groups?.[groupCode]
  }

  function getItems(projectCode, groupCode) {
    return [...(getGroup(projectCode, groupCode)?.items || [])].sort(
      (a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0),
    )
  }

  async function requestProjects(projectCodes, options = {}) {
    const codes = normalizeProjectCodes(projectCodes)
    if (!codes.length) return projects.value

    const langCode = getDictionaryLanguageCode(options.langCode || i18n.global.locale.value)
    codes.forEach((code) => {
      loadingProjects[code] = true
      errors[code] = null
    })

    try {
      const res = await queryBusinessDictionary({
        projects: codes.map((projectCode) => {
          const version = options.force ? 0 : getProject(projectCode)?.version
          return {
            projectCode,
            ...(version === undefined ? {} : { version }),
            moduleCodes: [],
            groupCodes: [],
          }
        }),
      })

      if (!isResponseSuccess(res)) {
        throw new Error(res?.msg || res?.message || 'Business dictionary request failed')
      }

      // 快速切换语言时，已过期语言的响应不得覆盖当前页面语言和缓存。
      if (langCode !== getDictionaryLanguageCode(i18n.global.locale.value)) {
        return projects.value
      }

      const responseProjects = res?.data?.projects || res?.data?.data?.projects || {}
      let hasSetAction = false
      codes.forEach((projectCode) => {
        const responseProject = responseProjects[projectCode]
        const action = String(responseProject?.action || '').toLowerCase()
        if (action === 'set') {
          hasSetAction = true
          projects.value[projectCode] = {
            version: responseProject.version,
            groups: normalizeGroups(responseProject.groups),
          }
        }
        if (!checkedProjects.value.includes(projectCode)) {
          checkedProjects.value.push(projectCode)
        }
      })

      if (hasSetAction || cacheLangCode.value === langCode) {
        cacheLangCode.value = langCode
        persist()
      }
      return projects.value
    } catch (error) {
      codes.forEach((projectCode) => {
        errors[projectCode] = error
      })
      throw error
    }
  }

  function startRequest(projectCodes, options = {}) {
    const codes = normalizeProjectCodes(projectCodes)
    const langCode = getDictionaryLanguageCode(options.langCode || i18n.global.locale.value)
    let task
    task = requestProjects(codes, { ...options, langCode }).finally(() => {
      codes.forEach((projectCode) => {
        if (inflightProjects.get(projectCode)?.promise === task) {
          inflightProjects.delete(projectCode)
          loadingProjects[projectCode] = false
        }
      })
    })

    const requestInfo = { promise: task, langCode, force: Boolean(options.force) }
    codes.forEach((projectCode) => inflightProjects.set(projectCode, requestInfo))
    return task
  }

  function refreshProjects(projectCodes, options = {}) {
    const codes = normalizeProjectCodes(projectCodes)
    codes.forEach((projectCode) => trackedProjects.add(projectCode))
    if (!codes.length) return Promise.resolve(projects.value)

    const langCode = getDictionaryLanguageCode(options.langCode || i18n.global.locale.value)
    const force = Boolean(
      options.force ||
      (Object.keys(projects.value).length && cacheLangCode.value !== langCode),
    )
    const pendingEntries = codes.map((code) => inflightProjects.get(code)).filter(Boolean)
    const reusableRequest = pendingEntries.find(
      (entry) => entry.langCode === langCode && (!force || entry.force),
    )

    if (reusableRequest && codes.every((code) => inflightProjects.get(code) === reusableRequest)) {
      return reusableRequest.promise
    }

    if (pendingEntries.length) {
      const pendingPromises = [...new Set(pendingEntries.map((entry) => entry.promise))]
      return Promise.allSettled(pendingPromises).then(() => {
        if (langCode !== getDictionaryLanguageCode(i18n.global.locale.value)) {
          return projects.value
        }
        return refreshProjects(codes, { ...options, force, langCode })
      })
    }

    return startRequest(codes, { ...options, force, langCode })
  }

  async function refreshForLanguage(language) {
    const langCode = getDictionaryLanguageCode(language)
    const refreshId = ++languageRefreshId
    checkedProjects.value = []

    const codes = normalizeProjectCodes([
      ...trackedProjects,
      ...Object.keys(projects.value),
      ...inflightProjects.keys(),
      ...getConfiguredDictionaryProjects(),
    ])
    if (!codes.length) return projects.value

    const pendingPromises = [
      ...new Set(
        codes
          .map((projectCode) => inflightProjects.get(projectCode)?.promise)
          .filter(Boolean),
      ),
    ]
    if (pendingPromises.length) await Promise.allSettled(pendingPromises)

    if (
      refreshId !== languageRefreshId ||
      langCode !== getDictionaryLanguageCode(i18n.global.locale.value)
    ) {
      return projects.value
    }

    return refreshProjects(codes, { force: true, langCode })
  }

  function ensureProject(projectCode) {
    trackedProjects.add(projectCode)
    const langCode = getDictionaryLanguageCode(i18n.global.locale.value)
    if (
      checkedProjects.value.includes(projectCode) &&
      cacheLangCode.value === langCode
    ) {
      return Promise.resolve(getProject(projectCode))
    }
    return refreshProjects(projectCode, { langCode }).then(() => getProject(projectCode))
  }

  function ensureProjects(projectCodes) {
    const langCode = getDictionaryLanguageCode(i18n.global.locale.value)
    const uncheckedCodes = normalizeProjectCodes(projectCodes).filter(
      (projectCode) =>
        !checkedProjects.value.includes(projectCode) || cacheLangCode.value !== langCode,
    )
    if (!uncheckedCodes.length) return Promise.resolve(projects.value)
    return refreshProjects(uncheckedCodes, { langCode })
  }

  watch(
    () => getDictionaryLanguageCode(i18n.global.locale.value),
    (langCode, oldLangCode) => {
      if (langCode === oldLangCode) return
      refreshForLanguage(langCode).catch(() => {})
    },
  )

  const currentLangCode = getDictionaryLanguageCode(i18n.global.locale.value)
  if (Object.keys(projects.value).length && cacheLangCode.value !== currentLangCode) {
    refreshForLanguage(currentLangCode).catch(() => {})
  }

  return {
    projects,
    checkedProjects,
    loadingProjects,
    errors,
    getProject,
    getGroup,
    getItems,
    refreshProjects,
    refreshForLanguage,
    ensureProject,
    ensureProjects,
  }
})

function normalizeGroups(groups) {
  if (!Array.isArray(groups)) return groups || {}
  return Object.fromEntries(
    groups
      .map((group) => [group.groupCode || group.code, group])
      .filter(([groupCode]) => Boolean(groupCode)),
  )
}

export default useBusinessDictStore
